import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const jobFormSchema = z.object({
  title: z.string().min(1, { message: 'Job title is required' }),
  department: z.string().min(1, { message: 'Department is required' }),
  location: z.string().min(1, { message: 'Location is required' }),
  employment_type: z.string().min(1, { message: 'Employment type is required' }),
  experience_level: z.string().min(1, { message: 'Experience level is required' }),
  salary_range: z.string().optional(),
  description: z.string().min(1, { message: 'Job description is required' }),
  requirements: z.string().min(1, { message: 'Job requirements are required' }),
  benefits: z.string().optional(),
  remote_option: z.enum(['onsite', 'hybrid', 'remote']),
  is_active: z.boolean().default(true),
  skills: z.array(z.string()).min(1, { message: 'At least one skill is required' }),
  application_deadline: z.string().optional().nullable(),
  application_url: z.string().url({ message: 'Please enter a valid URL' }).optional().or(z.literal('')),
  application_email: z.string().email({ message: 'Please enter a valid email' }).optional().or(z.literal('')),
  company_id: z.string().optional(),
  added_by_user_id: z.string().optional(),
  added_source: z.string().optional(),
  added_at: z.string().optional()
});

type FormValues = z.infer<typeof jobFormSchema>;

interface RecruitingProjectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: any;
  onSuccess: () => void;
  userCompanies?: any[];
}

const defaultInterviewStages = [
  { 
    stage_name: 'Initial Screening',
    stage_description: 'Brief phone call to assess basic qualifications and mutual fit',
    stage_duration: '30 minutes'
  },
  {
    stage_name: 'Technical Interview',
    stage_description: 'In-depth technical discussion and problem-solving session',
    stage_duration: '1 hour'
  },
  {
    stage_name: 'Team Interview',
    stage_description: 'Meet with potential team members and discuss collaboration',
    stage_duration: '1 hour'
  }
];

export default function RecruitingProjectForm({ 
  open, 
  onOpenChange, 
  project, 
  onSuccess,
  userCompanies = []
}: RecruitingProjectFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('job-details');
  const [newSkill, setNewSkill] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const isEditing = !!project;

  const form = useForm<FormValues>({
    resolver: zodResolver(jobFormSchema),
    defaultValues: {
      title: '',
      department: '',
      location: '',
      employment_type: 'full-time',
      experience_level: 'mid-level',
      salary_range: '',
      description: '',
      requirements: '',
      benefits: '',
      remote_option: 'onsite',
      is_active: true,
      skills: [],
      application_deadline: null,
      application_url: '',
      application_email: '',
      company_id: '',
      added_by_user_id: user?.id,
      added_source: 'client',
      added_at: new Date().toISOString()
    },
  });

  // Fetch user's companies
  useEffect(() => {
    if (user && open) {
      if (userCompanies.length > 0) {
        setAvailableCompanies(userCompanies);
        
        // Set default company if available
        if (userCompanies.length > 0 && !selectedCompany) {
          const defaultCompany = userCompanies[0].id;
          setSelectedCompany(defaultCompany);
          form.setValue('company_id', defaultCompany);
        }
      } else {
        fetchUserCompanies();
      }
    }
  }, [user, open, userCompanies]);

  // Set form values if editing an existing project
  useEffect(() => {
    if (isEditing && project && open) {
      form.reset({
        title: project.title || '',
        department: project.department || '',
        location: project.location || '',
        employment_type: project.employment_type || 'full-time',
        experience_level: project.experience_level || 'mid-level',
        salary_range: project.salary_range || '',
        description: project.description || '',
        requirements: project.requirements || '',
        benefits: project.benefits || '',
        remote_option: project.remote_option || 'onsite',
        is_active: project.is_active !== undefined ? project.is_active : true,
        skills: project.skills || [],
        application_deadline: project.application_deadline ? new Date(project.application_deadline).toISOString().split('T')[0] : null,
        application_url: project.application_url || '',
        application_email: project.application_email || '',
        company_id: project.company_id || '',
        added_by_user_id: project.added_by_user_id || user?.id,
        added_source: project.added_source || 'client',
        added_at: project.added_at || new Date().toISOString()
      });
      
      if (project.company_id) {
        setSelectedCompany(project.company_id);
      }
    } else if (open) {
      form.reset({
        title: '',
        department: '',
        location: '',
        employment_type: 'full-time',
        experience_level: 'mid-level',
        salary_range: '',
        description: '',
        requirements: '',
        benefits: '',
        remote_option: 'onsite',
        is_active: true,
        skills: [],
        application_deadline: null,
        application_url: '',
        application_email: '',
        company_id: selectedCompany || '',
        added_by_user_id: user?.id,
        added_source: 'client',
        added_at: new Date().toISOString()
      });
    }
  }, [open, project, isEditing, selectedCompany]);

  const fetchUserCompanies = async () => {
    try {
      // First check for company_users associations
      const { data: companyUsers, error: companyUsersError } = await supabase
        .from('company_users')
        .select('company_id, companies(id, name)')
        .eq('user_id', user?.id)
        .eq('is_active', true);

      if (companyUsersError) throw companyUsersError;
      
      // Also check for primary_company_id in profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('primary_company_id, company_name')
        .eq('id', user?.id)
        .single();
        
      if (profileError && profileError.code !== 'PGRST116') throw profileError;
      
      // Combine both sources of company information
      const companies = [];
      
      // Add companies from company_users
      if (companyUsers && companyUsers.length > 0) {
        companyUsers.forEach(cu => {
          if (cu.companies) {
            companies.push({
              id: cu.company_id,
              name: cu.companies.name
            });
          }
        });
      }
      
      // Add primary company from profile if not already included
      if (profile?.primary_company_id && !companies.some(c => c.id === profile.primary_company_id)) {
        companies.push({
          id: profile.primary_company_id,
          name: profile.company_name || 'My Company'
        });
      }
      
      setAvailableCompanies(companies);
      
      // Set default company if available
      if (companies.length > 0) {
        const defaultCompany = companies[0].id;
        setSelectedCompany(defaultCompany);
        form.setValue('company_id', defaultCompany);
      }
    } catch (error) {
      console.error('Error fetching user companies:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load company information'
      });
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Ensure company_id is set
      if (!data.company_id && selectedCompany) {
        data.company_id = selectedCompany;
      }
      
      // Set metadata fields
      data.added_by_user_id = user.id;
      data.added_source = 'client';
      data.added_at = new Date().toISOString();
      
      const formattedData = {
        ...data,
        profile_id: user.id,
        company_id: data.company_id || selectedCompany,
        application_deadline: data.application_deadline && data.application_deadline.trim() !== '' 
          ? data.application_deadline 
          : null,
        created_at: isEditing ? undefined : new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      let error;
      
      if (isEditing) {
        const { error: updateError } = await supabase
          .from('recruiting_projects')
          .update(formattedData)
          .eq('id', project.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('recruiting_projects')
          .insert([formattedData]);
        error = insertError;
      }

      if (error) {
        console.error("Supabase error:", error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to save recruiting project',
        });
      } else {
        toast({
          title: 'Success',
          description: `Recruiting project ${isEditing ? 'updated' : 'created'} successfully`,
        });
        onOpenChange(false);
        onSuccess();
      }
    } catch (error: any) {
      console.error("Unexpected error:", error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addSkill = () => {
    if (!newSkill.trim()) return;
    
    const currentSkills = form.getValues('skills') || [];
    if (!currentSkills.includes(newSkill.trim())) {
      form.setValue('skills', [...currentSkills, newSkill.trim()]);
      form.trigger('skills'); // Trigger validation after updating
    }
    setNewSkill('');
  };

  const removeSkill = (skill: string) => {
    const currentSkills = form.getValues('skills') || [];
    form.setValue('skills', currentSkills.filter(s => s !== skill));
    form.trigger('skills'); // Trigger validation after updating
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Recruiting Project' : 'Create Recruiting Project'}</DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update your recruiting project details below.' 
              : 'Set up a new recruiting project with job details, requirements, and interview process.'}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="job-details">Job Details</TabsTrigger>
                <TabsTrigger value="job-requirements">Requirements & Skills</TabsTrigger>
                <TabsTrigger value="application-settings">Application Settings</TabsTrigger>
              </TabsList>
              
              {/* Job Details Tab */}
              <TabsContent value="job-details" className="space-y-4 mt-4">
                {availableCompanies.length > 0 && (
                  <FormField
                    control={form.control}
                    name="company_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company*</FormLabel>
                        <Select
                          value={field.value || selectedCompany || ''}
                          onValueChange={(value) => {
                            field.onChange(value);
                            setSelectedCompany(value);
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select company" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {availableCompanies.map((company) => (
                              <SelectItem key={company.id} value={company.id}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Job Title*</FormLabel>
                        <FormControl>
                          <Input placeholder="Senior Software Engineer" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department*</FormLabel>
                        <FormControl>
                          <Input placeholder="Engineering" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location*</FormLabel>
                        <FormControl>
                          <Input placeholder="San Francisco, CA" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="remote_option"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Work Type*</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select work type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="onsite">On-site</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                            <SelectItem value="remote">Remote</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="employment_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employment Type*</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select employment type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="full-time">Full-time</SelectItem>
                            <SelectItem value="part-time">Part-time</SelectItem>
                            <SelectItem value="contract">Contract</SelectItem>
                            <SelectItem value="internship">Internship</SelectItem>
                            <SelectItem value="temporary">Temporary</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="experience_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Experience Level*</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select experience level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="entry-level">Entry Level</SelectItem>
                            <SelectItem value="mid-level">Mid Level</SelectItem>
                            <SelectItem value="senior">Senior</SelectItem>
                            <SelectItem value="lead">Lead</SelectItem>
                            <SelectItem value="executive">Executive</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="salary_range"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Salary Range</FormLabel>
                      <FormControl>
                        <Input placeholder="$100,000 - $130,000" {...field} />
                      </FormControl>
                      <FormDescription>
                        Optional but recommended for better candidate matching
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Description*</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Describe the role, responsibilities, and what a typical day looks like..." 
                          className="min-h-[120px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex justify-between mt-4">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => setActiveTab('job-requirements')}>
                    Next: Requirements & Skills
                  </Button>
                </div>
              </TabsContent>
              
              {/* Requirements & Skills Tab */}
              <TabsContent value="job-requirements" className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="requirements"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Requirements*</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="List the required qualifications, education, and experience..." 
                          className="min-h-[150px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        Include education, years of experience, certifications, etc.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="space-y-2">
                  <FormLabel>Required Skills*</FormLabel>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a required skill"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addSkill();
                        }
                      }}
                    />
                    <Button type="button" onClick={addSkill}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.watch('skills')?.map((skill, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        {skill}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 ml-1"
                          onClick={() => removeSkill(skill)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  {form.formState.errors.skills && (
                    <p className="text-sm font-medium text-destructive">{form.formState.errors.skills.message}</p>
                  )}
                </div>
                
                <FormField
                  control={form.control}
                  name="benefits"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Benefits & Perks</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Health insurance, 401(k), flexible work hours, etc." 
                          className="min-h-[80px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex justify-between mt-4">
                  <Button type="button" onClick={() => setActiveTab('job-details')}>
                    Back: Job Details
                  </Button>
                  <Button type="button" onClick={() => setActiveTab('application-settings')}>
                    Next: Application Settings
                  </Button>
                </div>
              </TabsContent>
              
              {/* Application Settings Tab */}
              <TabsContent value="application-settings" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Application Settings</h3>
                  
                  <FormField
                    control={form.control}
                    name="application_deadline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Application Deadline</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field} 
                            value={field.value || ''} 
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional. Leave blank for no deadline.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="application_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>External Application URL</FormLabel>
                          <FormControl>
                            <Input placeholder="https://careers.example.com/apply" {...field} />
                          </FormControl>
                          <FormDescription>
                            Optional external application link
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="application_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Application Email</FormLabel>
                          <FormControl>
                            <Input placeholder="careers@example.com" {...field} />
                          </FormControl>
                          <FormDescription>
                            Optional email for applications
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                
                <DialogFooter className="flex justify-between mt-6 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setActiveTab('job-requirements')}>
                    Back: Requirements
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isEditing ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEditing ? 'Update Project' : 'Create Project'
                    )}
                  </Button>
                </DialogFooter>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}